import { endOfDay, startOfDay, startOfYesterday } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import { Sort, State } from 'api/endpoints/intel/intel.enums';
import { useAuth } from 'contexts/auth';

import type { RivalGroupRivalItemType } from 'api/api.types';
import type { IntelQueryParamsType } from 'api/endpoints/intel/intel.types';
import type { IntelModelCountersType } from 'store/models/intel-cards/intelCards.model';
import type { Dispatch, RootState } from 'store/store.types';

export type AlertBoard = {
  id: number;
  label: string;
  iconUrl: string;
  profileId: number;
  filters: {
    rivalIds: number;
  };
  count: number;
};

const MAX_ALERT_BOARDS = 3;

const useAlertDashboard = () => {
  const dispatch = useDispatch<Dispatch>();
  const { user } = useAuth();

  const counters = useSelector<RootState, IntelModelCountersType>(
    (state) => state.intelCards.counters,
  );
  const rivals = useSelector<RootState, RivalGroupRivalItemType[]>((state) => [
    ...state.rivals.byId.values(),
  ]);
  const rivalsMapByProfileId = useSelector<RootState, Map<string, string>>(
    (state) => state.rivals.byProfileId,
  );
  const isLoadingAlertsCount = useSelector<RootState, boolean>(
    (state) => state.intelCards.isLoading,
  );

  const curatingProfiles = user?.curatingProfiles.map((c) => {
    return rivalsMapByProfileId.get(c.toString());
  });

  const transformRivalToBoard = (
    rival: RivalGroupRivalItemType,
  ): AlertBoard => ({
    id: rival.id,
    label: rival.name,
    iconUrl: rival.iconUrl,
    filters: { rivalIds: rival.id },
    profileId: rival.profileId,
    count: counters.filters.rivalCount?.[rival.id] || 0,
  });

  const sortBoardsByCount = (boards: AlertBoard[]): AlertBoard[] =>
    boards.sort((a, b) => b.count - a.count);
  const getTopBoards = (boards: AlertBoard[], limit: number): AlertBoard[] =>
    boards.slice(0, limit);

  const boards = getTopBoards(
    sortBoardsByCount(
      rivals
        .filter((r) => curatingProfiles?.includes(r.id.toString()))
        .map(transformRivalToBoard),
    ),
    MAX_ALERT_BOARDS,
  );

  const sortedAlertBoards = boards.filter((s) => s.count > 0);

  // Fetch alerts count
  const getAlertsCount = () => {
    const today = startOfDay(new Date());
    const filters: IntelQueryParamsType = {
      states: [State.Unprocessed],
      sort: Sort.NEWEST,
      createdAfter: startOfYesterday().toISOString(),
      createdBefore: endOfDay(today).toISOString(),
      view: 'myNewIntel',
      relatedAlerts: true,
    };

    dispatch.intelFilters?.setFilters(filters);

    dispatch.intelCards.fetchIntelList({
      filters,
      pagination: { offset: 0 },
      rehydrate: false,
      isInTriageMode: false,
    });
  };

  return {
    sortedAlertBoards,
    getAlertsCount,
    isLoadingAlertsCount,
  };
};

export default useAlertDashboard;
